import axios from 'axios'
import qs from 'qs'
import SparkMD5 from 'spark-md5'
import { dateStringToDateBR, formataDataYYYYMMDD } from '../utils/date-validator'
import { extrairMensagemErroApi } from '../utils/api-error.helper'
import { limparTexto, normalizaSexo } from '../utils/text-format'
import CONSTANTS from '../constants/constants'
import BaseMixin from './base.mixin'

const baseUrl = import.meta.env.VITE_URL_API

const mixin = {
  mixins: [BaseMixin],
  methods: {
    async getSpecialityListXPlus(isCreatedExam = false) {
      const res = await this.getClient().get('/core/XPlus/Speciality?IsCreateExam=' + isCreatedExam)
      const data = res.data || []
      return data.map(item => ({ value: item.specialityID, label: item.specialityName, requiredPatientData: item.isRequiredPatientData }))
    },

    async getCreateExamSpecialityList(specialityID = 434) {
      const res = await this.getClient().get(`/core/XPlus/Speciality?IsCreateExam=true&SpecialityID=${specialityID}`)
      const payload = res.data?.data ?? res.data ?? []
      const data = Array.isArray(payload) ? payload : Object.values(payload)
      return data.map(item => ({ value: item.specialityID, label: item.specialityName }))
    },

    async getExamSourceList(page = 1, limit = 50, search = '') {
      // Usa o endpoint das fontes do usuário logado (mesmo do seletor de fonte da navbar),
      // evitando o endpoint de Administrator que retorna 401 para usuários source.
      const params = { Page: page && page > 0 ? page : 1, PageSize: limit || 50 }
      if (search && String(search).trim()) params.Name = search.trim()
      const res = await this.getClient().get('/core/ExamSource/ExamSource/CurrentUserSourcesWithFilters', { params })
      return res.data?.data || []
    },

    async restoreCanceledExam(dicomPlusID) {
      const user = this.getFromLocalStorage('user')
      const { data } = await axios.put(`${baseUrl}/core/DicomPlus/${dicomPlusID}/RestoreCanceledExam`, null, {
        headers: { Authorization: user?.token, refreshToken: user?.refreshToken, 'Accept-Language': this.$i18n.locale }
      })
      return data?.data
    },

    async restoreFailedExam(dicomPlusID) {
      const user = this.getFromLocalStorage('user')
      const { data } = await axios.put(`${baseUrl}/core/DicomPlus/${dicomPlusID}/RestoreFailedTryCreateExam`, null, {
        headers: { Authorization: user?.token, refreshToken: user?.refreshToken, 'Accept-Language': this.$i18n.locale }
      })
      return data?.data
    },

    gerarMD5(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const md5 = SparkMD5.ArrayBuffer.hash(e.target.result)
          resolve(md5)
        }
        reader.onerror = reject
        reader.readAsArrayBuffer(file)
      })
    },

    async validarArquivosXPlus(arquivos) {
      try {
        const user = this.getFromLocalStorage('user')
        await axios.post(`${baseUrl}/core/XPlus/ValidateFiles`, arquivos, {
          headers: {
            Authorization: user?.token,
            refreshToken: user?.refreshToken,
            'Accept-Language': this.$i18n.locale
          }
        })
        return { success: true, errors: [] }
      } catch (e) {
        return { success: false, errors: e?.response?.data?.errors || [] }
      }
    },

    async criarExameDicomPlus({ fonteID, tipoRegistro, especialidadeID, files }) {
      try {
        const payload = {
          FonteID: fonteID,
          TipoRegistro: tipoRegistro,
          EspecialidadeID: especialidadeID,
          Files: files
        }
        const user = this.getFromLocalStorage('user')
        const { data } = await axios.post(`${baseUrl}/core/DicomPlus`, payload, {
          headers: {
            Authorization: user?.token,
            refreshToken: user?.refreshToken,
            'Accept-Language': this.$i18n.locale
          }
        })
        return { success: true, data }
      } catch (e) {
        return { success: false, message: this.extrairMensagemErroApi(e, 'Erro ao criar o exame') }
      }
    },

    extrairMensagemErroApi(e, mensagemPadrao = 'Ocorreu um erro') {
      return extrairMensagemErroApi(e, mensagemPadrao)
    },

    async getDadosComplementares(fonteID) {
      const res = await this.getClient().get(`/core/PtmSyncPlus/${fonteID}`)
      return res.data?.data || []
    },

    async getDadosPorPacienteXPlus(dicomPlusID) {
      const res = await this.getClient().get('/core/DicomPlus', { params: { DicomPlusID: dicomPlusID, tiporegistro: 2 } })
      const items = res.data?.data || []
      return items.map(item => ({
        dicomPlusID: item.dicomPlusID,
        exameID: item.exameID,
        unidade: item.sourceRequestorName,
        especialidade: item.especialidadeID,
        comentarios: item.patientComments,
        paciente: item.patientName,
        socialName: item.patientSocialName,
        dataNascimento: dateStringToDateBR(item.patientBirthDate),
        sexo: normalizaSexo(item.patientSex),
        patientID: item.patientID,
        isExamNotExistGestorSaude: !!item.isExamNotExistGestorSaude,
        requisicaoID: item.requisicaoID != null ? String(item.requisicaoID) : '',
        prioridade: item.prioridade
      }))
    },

    async removeAssignedUser(dicomPlusID) {
      await this.getClient().delete(`/core/DicomPlus/RemoveUserChanging/${dicomPlusID}`)
    },

    async updatePacienteXPlus(payload) {
      this.bypass = true
      try {
        const res = await this.getClient().put('/core/DicomPlus', payload)
        if (res && res.isAxiosError) {
          const data = (res.response && res.response.data) || {}
          const msg =
            data.message ||
            (Array.isArray(data.errors)
              ? data.errors.map((e) => (e && e.message) || e).filter(Boolean).join('\n')
              : typeof data.errors === 'string'
                ? data.errors
                : '') ||
            res.message ||
            'Erro ao atualizar dados'
          const err = new Error(msg)
          err.errors = msg
          throw err
        }
        return { success: res.status === 200, message: res.data?.message }
      } finally {
        this.bypass = false
      }
    },

    async buscarDadosPacienteGestorSaude({ solicitacaoId, modalityID, fonteID, pacienteCpf, pacienteDataNascimento }) {
      try {
        if (!solicitacaoId) throw new Error('O parâmetro Solicitação ID é obrigatório.')

        const params = new URLSearchParams()
        params.append('solicitacao_id', solicitacaoId)
        params.append('modalityID', modalityID)
        params.append('FonteID', fonteID)

        if (pacienteCpf) { params.append('paciente_cpf', limparTexto(pacienteCpf)) }
        if (pacienteDataNascimento) { params.append('paciente_data_nascimento', formataDataYYYYMMDD(pacienteDataNascimento)) }

        const res = await this.getClient().get(`/core/integration/gestorSaude?${params.toString()}`)
        const dados = res.data.data
        if (Array.isArray(dados) && dados.length > 0) {
          return dados[0]
        }
        return { mensagem: 'Nenhum registro encontrado' }
      } catch (error) {
        throw error.response?.data?.errors
      }
    },

    async getSocFiles({ cpf, page, pageSize, fonteID, patientName, allFiles }) {
      const params = { cpf, page, pageSize, fonteID, patientName }
      if (allFiles) params.allFiles = true

      const res = await this.getClient().get('/core/soc', {
        params,
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' })
      })

      const apiData = res.data.data
      const items = Array.isArray(apiData.data)
        ? apiData.data.map(item => ({
          ...item,
          portalSocID: item.portalSocId,
          empresaNome: item.companyName,
          pacienteNome: item.name,
          especialidadeCodigo: item.specialityCode,
          especialidadeNome: item.specialityName
        }))
        : []

      const total = apiData.total || 0
      const pageCount = apiData.pageCount || Math.ceil(total / pageSize)

      return { items, total, pageCount }
    },

    async getPatientAge(birthDate) {
      try {
        const res = await this.getClient().get(`/core/DicomPlus/PatientAge/${birthDate}`)
        return res.data
      } catch {
        return { success: false }
      }
    },

    async getFileList(dicomPlusID) {
      const res = await this.getClient().get(`/core/DicomPlus/File/${dicomPlusID}`)
      return res.data || []
    },

    getFileTypes() {
      const tipos = [
        { value: 1, label: 'Exame anterior' },
        { value: 2, label: 'Anamnese' },
        { value: 3, label: 'Laudo anterior' },
        { value: 4, label: 'Pedido médico' },
        { value: 5, label: 'Exame criado' }
      ]
      // Pedido médico é o anexo mais usado: fica fixo no topo e os demais em ordem alfabética.
      const pedidoMedico = tipos.filter(tipo => tipo.value === CONSTANTS.DICOM_PLUS_TIPO_ANEXO.PEDIDO_MEDICO)
      const demais = tipos
        .filter(tipo => tipo.value !== CONSTANTS.DICOM_PLUS_TIPO_ANEXO.PEDIDO_MEDICO)
        .sort((a, b) => a.label.localeCompare(b.label))
      return [...pedidoMedico, ...demais]
    },

    async criarUrlUpload(file) {
      try {
        const { data } = await this.getClient().post('/core/DicomPlus/File/CreateUploadUrl', {
          fileName: file.name,
          fileType: file.type || 'application/octet-stream'
        })
        return { success: true, uploadUrl: data.data.signedUrl, publicUrl: data.data.objectPath }
      } catch (e) {
        const msg = e?.message || e?.response?.data?.message || ''
        const isNetworkError =
          msg.toLowerCase().includes('network') ||
          msg.toLowerCase().includes('timeout') ||
          e.code === 'ECONNABORTED'
        return {
          success: false,
          message: isNetworkError ? 'Verifique a Internet' : msg || 'Erro ao fazer upload do arquivo'
        }
      }
    },

    async uploadStorage(uploadUrl, file) {
      try {
        await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } })
        return { success: true }
      } catch (e) {
        const msg = e?.message || e?.response?.data?.message || ''
        const isNetworkError =
          msg.toLowerCase().includes('network') ||
          msg.toLowerCase().includes('timeout') ||
          e.code === 'ECONNABORTED'
        return {
          success: false,
          message: isNetworkError ? 'Verifique a Internet' : msg || 'Erro ao fazer upload do arquivo'
        }
      }
    },

    async salvarAnexo(payload) {
      try {
        const { data } = await this.getClient().post('/core/DicomPlus/File', payload)
        return { success: true, data }
      } catch (e) {
        throw e.response?.data?.errors
      }
    },

    async deleteFile(dicomPlusFileID) {
      await this.getClient().delete(`/core/DicomPlus/File/${dicomPlusFileID}`)
    }
  }
}

export default mixin
